import type {
  FieldDefinition,
  GroupDefinition,
  ResolvedDefinition,
  SectionDefinition,
  SubmissionPayload,
} from '../schema/types.js';
import { isUnknown } from '../schema/types.js';
import { isValidAbnOrAcn } from './abn.js';

/**
 * Validation, shared by the portal and the API.
 *
 * The portal runs this for convenience. The API runs it again against the FROZEN
 * definition and that run is the gate — see docs/form-definition.md. Sharing the
 * code is what stops the two drifting apart.
 */

export interface ValidationError {
  /** Dotted `groupKey.fieldKey`, with `[i]` on repeatable groups. */
  path: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[+()\d\s-]{6,20}$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/*
  Today as yyyy-MM-dd in the runner's local time.

  Local rather than UTC on purpose: a customer in Australia entering "today"
  late in the evening is ahead of UTC, and a UTC comparison would reject a date
  they are looking at on their own calendar. The portal runs this in their
  browser; the API re-runs it in Azure, so the API is the lenient one by up to a
  day. That asymmetry is the right way round — the gate must never reject a
  date the customer could legitimately enter.
*/
function todayIso(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/**
 * Has an externalAction actually been recorded?
 *
 * "Recorded" means every required sub-field is filled — not that the customer
 * ticked something. Used to decide whether the section counts as complete.
 */
export function isExternalActionRecorded(
  field: FieldDefinition,
  value: unknown,
): boolean {
  if (field.type !== 'externalAction') return true;
  if (value === undefined || value === null || typeof value !== 'object') return false;

  const provided = value as Record<string, unknown>;
  return (field.actionFields ?? [])
    .filter((f) => f.required)
    .every((f) => !isBlank(provided[f.key]));
}

/**
 * Is a section fully done, or valid-but-waiting?
 *
 * Distinct from `valid`. A section can pass validation and still not be
 * finished: the agreements section is submittable with the Microsoft Customer
 * Agreement unrecorded, because it is collected late by design and blocking on
 * it would strand the whole onboarding. Such a section stays In Progress rather
 * than going Submitted, so it keeps showing up as outstanding work for staff.
 *
 * Only externalAction fields can make a section incomplete. Everything else is
 * either required — and therefore already enforced by validation — or optional.
 */
export function isSectionComplete(
  definition: ResolvedDefinition,
  sectionKey: string,
  values: Record<string, unknown>,
): boolean {
  const section = definition.sections.find((s) => s.key === sectionKey);
  if (!section) return false;

  for (const group of section.groups) {
    if (group.repeatable) continue; // externalAction is never inside a grid row
    for (const field of group.fields) {
      if (field.type !== 'externalAction') continue;
      if (!isExternalActionRecorded(field, values[`${group.key}.${field.key}`])) return false;
    }
  }

  return true;
}

function isBlank(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  // An empty multi-select or grid is "nothing provided", not "provided, empty".
  // Without this a required multichoice is satisfied by ticking nothing.
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function validateField(field: FieldDefinition, value: unknown, path: string): ValidationError[] {
  const errors: ValidationError[] = [];
  const fail = (message: string) => errors.push({ path, message });

  // "I don't know" satisfies required. In the IT section especially, forcing a
  // guess produces worse data than an honest gap.
  if (isUnknown(value)) {
    if (!field.allowUnknown) {
      fail(`${field.label} cannot be marked as unknown.`);
    }
    return errors;
  }

  // Link fields are display-only and record nothing. Checked before the blank
  // test, because a link always has an empty value and would otherwise report
  // itself missing.
  if (field.type === 'link') return errors;

  /*
    An external action is either done or not.

    Not done is legitimate and must stay submittable — the MCA is deliberately
    collected late, and blocking the section on it strands anyone who cannot
    accept yet. What is not legitimate is half a record: a signer name with no
    date is not something our indirect provider can attest from. So the
    sub-fields' own `required` flags apply only once the customer has started
    filling it in.

    Whether the section counts as complete is a separate question, answered by
    isExternalActionRecorded and the API's submit handler.
  */
  if (field.type === 'externalAction') {
    const sub = field.actionFields ?? [];
    const provided = (value ?? {}) as Record<string, unknown>;

    if (typeof provided !== 'object' || Array.isArray(provided)) {
      fail(`${field.label} is malformed.`);
      return errors;
    }

    const started = sub.some((f) => !isBlank(provided[f.key]) || isUnknown(provided[f.key]));
    if (!started) return errors;

    for (const subField of sub) {
      errors.push(
        ...validateField(subField, provided[subField.key], `${path}.${subField.key}`),
      );
    }
    return errors;
  }

  /*
    A consent tick. `true` or nothing — never a string, and never "false"
    meaning "asked and declined".

    Checked before the blank test because an unticked box is `undefined`, and
    the generic "is required" message is wrong here: you do not "fill in" a
    statement, you agree to it or you do not. Someone who does not agree needs
    to be told to talk to us, not told they missed a field.
  */
  if (field.type === 'consent') {
    if (value !== true && field.required) {
      fail(
        'We cannot set up your support without this. If you are not able to agree to it, ' +
          'leave it unticked and reply to the email we sent — we will talk it through.',
      );
    }
    return errors;
  }

  if (isBlank(value)) {
    if (field.required) fail(`${field.label} is required.`);
    return errors;
  }

  if (typeof value === 'string' && field.maxLength && value.length > field.maxLength) {
    fail(`${field.label} must be ${field.maxLength} characters or fewer.`);
  }

  switch (field.type) {
    case 'email':
      if (typeof value !== 'string' || !EMAIL.test(value)) {
        fail(`${field.label} does not look like an email address.`);
      }
      break;

    case 'phone':
      if (typeof value !== 'string' || !PHONE.test(value)) {
        fail(`${field.label} does not look like a phone number.`);
      }
      break;

    case 'abn':
      if (typeof value !== 'string' || !isValidAbnOrAcn(value)) {
        fail(`${field.label} is not a valid ABN or ACN. Check for a typo.`);
      }
      break;

    case 'date':
      if (typeof value !== 'string' || !ISO_DATE.test(value) || Number.isNaN(Date.parse(value))) {
        fail(`${field.label} is not a valid date.`);
      } else if (field.notFuture && value > todayIso()) {
        // String compare is safe and timezone-free on ISO yyyy-MM-dd.
        fail(`${field.label} cannot be in the future.`);
      }
      break;

    case 'number':
      if (typeof value !== 'number' || Number.isNaN(value)) {
        fail(`${field.label} must be a number.`);
      }
      break;

    case 'choice': {
      const allowed = (field.options ?? []).map((o) => o.value);
      if (typeof value !== 'string' || !allowed.includes(value)) {
        fail(`${field.label} is not one of the available options.`);
      }
      break;
    }

    case 'multichoice': {
      if (!Array.isArray(value)) {
        fail(`${field.label} is malformed.`);
        break;
      }
      const allowed = (field.options ?? []).map((o) => o.value);
      const unknownOptions = value.filter((v) => typeof v !== 'string' || !allowed.includes(v));
      if (unknownOptions.length > 0) {
        fail(`${field.label} contains an option that is not available.`);
      }
      if (new Set(value).size !== value.length) {
        fail(`${field.label} has the same option twice.`);
      }
      break;
    }

    case 'grid': {
      if (!Array.isArray(value)) {
        fail(`${field.label} must be a list of rows.`);
        break;
      }

      const columns = field.columns ?? [];

      value.forEach((row, index) => {
        const rowValues = (row ?? {}) as Record<string, unknown>;

        // A grid row the customer started and abandoned is not an error —
        // pasting from a spreadsheet routinely leaves trailing blanks.
        const empty = columns.every((column) => isBlank(rowValues[column.key]));
        if (empty) return;

        for (const column of columns) {
          const cell = rowValues[column.key];
          if (isBlank(cell)) continue;

          if (column.type === 'choice') {
            const allowed = (column.options ?? []).map((o) => o.value);
            if (typeof cell !== 'string' || !allowed.includes(cell)) {
              // Names the row so it can be found in a grid of forty.
              fail(
                `${field.label} row ${index + 1}: "${String(cell)}" is not a valid ${column.label}. ` +
                  `Choose one of: ${allowed.join(', ')}.`,
              );
            }
          } else if (column.type === 'number') {
            if (typeof cell !== 'number' || Number.isNaN(cell)) {
              fail(`${field.label} row ${index + 1}: ${column.label} must be a number.`);
            }
          } else if (column.type === 'date') {
            if (typeof cell !== 'string' || !ISO_DATE.test(cell) || Number.isNaN(Date.parse(cell))) {
              fail(`${field.label} row ${index + 1}: ${column.label} is not a valid date.`);
            }
          }
        }
      });
      break;
    }

    default:
      if (typeof value !== 'string') {
        fail(`${field.label} must be text.`);
      }
  }

  return errors;
}

function validateGroup(group: GroupDefinition, values: Record<string, unknown>): ValidationError[] {
  const errors: ValidationError[] = [];

  if (group.repeatable) {
    const rows = values[group.key];

    if (rows === undefined) {
      // A repeatable group with a required field needs at least one entry.
      if (group.fields.some((f) => f.required)) {
        const noun = group.itemLabel ?? 'entry';
        errors.push({ path: group.key, message: `Add at least one ${noun}.` });
      }
      return errors;
    }

    if (!Array.isArray(rows)) {
      errors.push({ path: group.key, message: `${group.title} is malformed.` });
      return errors;
    }

    rows.forEach((row, index) => {
      const rowValues = (row ?? {}) as Record<string, unknown>;
      for (const field of group.fields) {
        errors.push(
          ...validateField(field, rowValues[field.key], `${group.key}[${index}].${field.key}`),
        );
      }
    });

    // Cross-row rule, e.g. exactly one contact is the primary. Counted across
    // all rows rather than per row, so the message points at the group.
    const rule = group.requireExactlyOne;
    if (rule) {
      const holders = rows.filter((row) => {
        const cell = (row ?? {})[rule.field];
        if (Array.isArray(cell)) return cell.includes(rule.value);
        return cell === rule.value;
      }).length;

      if (holders !== 1) {
        errors.push({ path: group.key, message: rule.message });
      }
    }

    return errors;
  }

  for (const field of group.fields) {
    errors.push(...validateField(field, values[`${group.key}.${field.key}`], `${group.key}.${field.key}`));
  }

  return errors;
}

/** Validate one section's payload against the frozen definition. */
export function validateSubmission(
  definition: ResolvedDefinition,
  payload: SubmissionPayload,
): ValidationResult {
  const errors: ValidationError[] = [];

  const section: SectionDefinition | undefined = definition.sections.find(
    (s) => s.key === payload.sectionKey,
  );

  if (!section) {
    return {
      valid: false,
      errors: [
        {
          path: '',
          message: `Section "${payload.sectionKey}" is not part of this onboarding.`,
        },
      ],
    };
  }

  if (isBlank(payload.submittedByName)) {
    errors.push({ path: 'submittedByName', message: 'Tell us your name so we know who completed this.' });
  }
  if (isBlank(payload.submittedByEmail) || !EMAIL.test(String(payload.submittedByEmail))) {
    errors.push({ path: 'submittedByEmail', message: 'We need a valid email address.' });
  }

  for (const group of section.groups) {
    errors.push(...validateGroup(group, payload.values));
  }

  return { valid: errors.length === 0, errors };
}
